/* eslint-disable global-require */
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'

// components
import StoreDownloadButtons from '../../../../components/StoreDownloadButtons/StoreDownloadButtons'

// utils
import { AppContext } from '../../../../utils/appProvider'

// styles
import * as SC from './AppFeatureSectionStyles'
import { HomePageContainer } from '../../HomePageStyles'
import { BREAKPOINTS_INTS } from '../../../../styles/constants'

// hooks
import useMessages from '../../../../hooks/useMessages'

// types
import { CmsHomepageData } from '../../../../types/types'
import { getLinkAttributes } from '../../../../utils/helper'
import { PRODUCTION_ENVIRONMENT } from '../../../../utils/enums'

type Props = {
	cmsData: CmsHomepageData['appFeatureData']
}

const getImage = (image: Props['cmsData']['image1'], productionEnvironment: PRODUCTION_ENVIRONMENT, locale: string) => {
	const { desktop, mobile, desktopWebP, mobileWebP, alt, link } = image

	const imageComponent = (
		<SC.Picture>
			{/* WebP variants */}
			{desktopWebP && <source srcSet={desktopWebP} type='image/webp' media={`(min-width: ${BREAKPOINTS_INTS.sm}px)`} />}
			{mobileWebP && <source srcSet={mobileWebP} type='image/webp' media={`(max-width: ${BREAKPOINTS_INTS.sm - 1}px)`} />}

			{/* JPEG fallback */}
			<source srcSet={desktop} media={`(min-width: ${BREAKPOINTS_INTS.sm}px)`} />
			<source srcSet={mobile} media={`(max-width: ${BREAKPOINTS_INTS.sm - 1}px)`} />

			{/* Fallback <img> tag for maximum compatibility */}
			<img src={desktop} alt={alt} loading='lazy' />
		</SC.Picture>
	)

	return (
		<SC.ImageContainer>
			{link ? (
				<SC.ImageLink href={link} {...getLinkAttributes(link, productionEnvironment, locale)}>
					{imageComponent}
				</SC.ImageLink>
			) : (
				imageComponent
			)}
		</SC.ImageContainer>
	)
}

// component
const AppFeatureSection = (props: Props) => {
	const { cmsData } = props
	const { title, subtitle, image1, image2, image3, image4, image5 } = cmsData

	const { assetsPath, productionEnvironment } = useContext(AppContext)
	const { locale } = useIntl()

	const { messages } = useMessages()

	const qrCode = `${assetsPath}/${require('../../../../assets/images/app-qr-code.jpg')}`

	return (
		<section>
			<HomePageContainer>
				<SC.InfoContainer>
					<SC.SectionTitle $marginBottom={'16px'} $textAlign={'center'}>
						{title}
					</SC.SectionTitle>
					<SC.SectionDescriptionDesktop $marginBottom={'16px'} $textAlign={'center'}>
						{subtitle.desktop}
					</SC.SectionDescriptionDesktop>
					<SC.SectionDescriptionMobile $marginBottom={'16px'} $textAlign={'center'}>
						{subtitle.mobile}
					</SC.SectionDescriptionMobile>
					<SC.DownloadsSectionWrapper>
						<SC.QRCodeImageWrapper>
							<img src={qrCode} width={86} height={86} alt={messages['Download Notino app via QR code']} loading='lazy' />
						</SC.QRCodeImageWrapper>
						<SC.StoreDownloadButtonsWrapper>
							<StoreDownloadButtons />
						</SC.StoreDownloadButtonsWrapper>
					</SC.DownloadsSectionWrapper>
				</SC.InfoContainer>
				<SC.Layout>
					{getImage(image1, productionEnvironment, locale)}
					{getImage(image2, productionEnvironment, locale)}
					{getImage(image3, productionEnvironment, locale)}
					{getImage(image4, productionEnvironment, locale)}
					{getImage(image5, productionEnvironment, locale)}
				</SC.Layout>
			</HomePageContainer>
		</section>
	)
}

export default AppFeatureSection
